import { ApplicationStatus, SavedJobPost } from "@/types/user";
import { Modal, Table } from "@/components/molecules";
import { useCompanyContext, useJobPostContext } from "@/providers/contexts";
import { useUpdateSavedJob, useUpdateSavedJobViewCount } from "@/hooks";
import { useEffect, useState } from "react";
import { Snackbar, Alert, TextField, IconButton } from "@mui/material";
import { Box } from "@/components/atoms";
import DeleteIcon from "@mui/icons-material/Delete";
import { State } from "@/providers/UserProvider";

const columns: {
  key: keyof SavedJobPost | "notes" | "is_active_job" | "questionnaire";
  label: string;
}[] = [
  { key: "is_active_job", label: "Job Active Status" },
  { key: "logo_url", label: "Logo" },
  { key: "company_name", label: "Company Name" },
  { key: "job_title", label: "Position Title" },
  { key: "application_status", label: "Application Status" },
  { key: "notes", label: "Notes" },
  { key: "questionnaire", label: "Questionnaire" },
];

interface SavedJobsTableProps {
  savedJobs: SavedJobPost[];
  refetchUser: State["refetchUser"];
  isDisabled: boolean;
}

export const SavedJobsTable = ({
  savedJobs,
  refetchUser,
  isDisabled,
}: SavedJobsTableProps) => {
  /* --------------------- Hooks --------------------- */
  const { setCompanyId, setCompanyModalOpen } = useCompanyContext();
  const { setJobPostId, setJobPostDetailsModalOpen } = useJobPostContext();
  const { updateSavedJobViewCount } = useUpdateSavedJobViewCount();

  const {
    updateSavedJob,
    error: updateError,
    data: updateData,
    loading,
  } = useUpdateSavedJob();

  const [activeJobPost, setActiveJobPost] = useState<SavedJobPost | null>(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [questionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState<
    Partial<SavedJobPost>
  >({
    application_status_comment: "",
    number_of_experienced_years: null,
    saved_reason: "",
  });

  const [snackBar, setSnackBar] = useState<{
    state: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ state: false, msg: "", severity: "success" });

  const isQuestionnaireIncomplete =
    !questionnaireData.application_status_comment?.trim() &&
    (questionnaireData.number_of_experienced_years === null ||
      questionnaireData.number_of_experienced_years === undefined) &&
    !questionnaireData.saved_reason?.trim();

  const isQuestionnaireComplete = (data: Partial<SavedJobPost>) => {
    return Boolean(
      data.application_status_comment?.trim() &&
        data.number_of_experienced_years !== null &&
        data.number_of_experienced_years !== undefined &&
        data.saved_reason?.trim(),
    );
  };

  /* --------------------- Handles --------------------- */

  const handleCompanyClick = (job: SavedJobPost) => {
    setCompanyId(job.company_id);
    setCompanyModalOpen(true);
  };

  const handleJobPostClick = (job: SavedJobPost) => {
    setJobPostId(job.job_post_id);
    setJobPostDetailsModalOpen(true);
    updateSavedJobViewCount({ saved_job_id: job.id });
  };

  const handleNoteClick = (savedJob: SavedJobPost) => {
    setActiveJobPost(savedJob);
    setNoteText(savedJob.notes || "");
    setNoteModalOpen(true);
  };

  const handleQuestionnaireClick = (job: SavedJobPost) => {
    setActiveJobPost(job);
    setQuestionnaireData({
      application_status_comment: job.application_status_comment || "",
      number_of_experienced_years: job.number_of_experienced_years || null,
      saved_reason: job.saved_reason || "",
    });
    setQuestionnaireModalOpen(true);
  };

  const handleSaveQuestionnaire = async () => {
    if (!activeJobPost) return;

    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      application_status_comment:
        questionnaireData.application_status_comment ?? null,
      number_of_experienced_years:
        questionnaireData.number_of_experienced_years !== undefined &&
        questionnaireData.number_of_experienced_years !== null
          ? questionnaireData.number_of_experienced_years
          : null,
      saved_reason: questionnaireData.saved_reason ?? null,
    });

    setQuestionnaireModalOpen(false);
    setActiveJobPost(null);
  };

  const handleDeleteApplicationStatusComment = async () => {
    if (!activeJobPost) return;
    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      application_status_comment: null,
    });
    setQuestionnaireData(prev => ({
      ...prev,
      application_status_comment: "",
    }));
  };

  const handleDeleteExperienceYears = async () => {
    if (!activeJobPost) return;
    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      number_of_experienced_years: null,
    });
    setQuestionnaireData(prev => ({
      ...prev,
      number_of_experienced_years: null,
    }));
  };

  const handleDeleteSavedReason = async () => {
    if (!activeJobPost) return;
    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      saved_reason: null,
    });
    setQuestionnaireData(prev => ({
      ...prev,
      saved_reason: "",
    }));
  };

  const handleSaveNote = async () => {
    if (!activeJobPost) return;
    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      notes: noteText,
    });
    setNoteModalOpen(false);
    setActiveJobPost(null);
  };

  const handleDeleteNote = async () => {
    if (!activeJobPost) return;
    await updateSavedJob({
      job_post_id: activeJobPost.job_post_id,
      notes: null,
    });
    setNoteModalOpen(false);
    setActiveJobPost(null);
  };

  const handleCloseNoteModal = () => {
    setNoteModalOpen(false);
    setActiveJobPost(null);
  };

  const handleApplicationStatusChange = async (
    jobPost: SavedJobPost,
    newStatus: ApplicationStatus,
  ) => {
    await updateSavedJob({
      job_post_id: jobPost.id,
      application_status: newStatus,
    });
  };

  useEffect(() => {
    if (updateData?.success) {
      setSnackBar({
        state: true,
        msg: updateData.message || "Note saved successfully.",
        severity: "success",
      });

      setTimeout(() => {
        refetchUser();
      }, 1000);
    }
  }, [updateData, refetchUser]);

  useEffect(() => {
    if (updateError) {
      setSnackBar({
        state: true,
        msg: updateError,
        severity: "error",
      });
    }
  }, [updateError]);

  return (
    <Box
      componentName="saved-jobs-table"
      border={false}
      sx={{ flex: 2, mt: 2 }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={4000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>

      {activeJobPost && noteModalOpen && (
        <Modal
          componentName="job-post-note"
          header={`Notes for ${activeJobPost?.job_title}`}
          open={noteModalOpen}
          renderButtonSet
          handleRightButtonClick={handleSaveNote}
          rightButtonText="Save Note"
          onCloseButtonClick={handleCloseNoteModal}
          leftButtonText="Cancel"
          disableRightButton={noteText.trim() === "" || loading}
        >
          <Box
            componentName="saved-job-note-row"
            border={false}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            display="flex"
            sx={{ mt: 2 }}
          >
            <TextField
              label="Note"
              multiline
              value={noteText}
              fullWidth
              onChange={e => setNoteText(e.target.value)}
              helperText="Add a reason to help remember why you saved this job post, what stood out to you, or just a simple note to help you organize your saved jobs."
            />
            <IconButton
              onClick={handleDeleteNote}
              size="large"
              disabled={noteText.trim() === "" || loading}
              sx={{
                cursor:
                  noteText.trim() === "" || loading ? "not-allowed" : "pointer",
                opacity: noteText.trim() === "" || loading ? 0.5 : 1,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Modal>
      )}

      {activeJobPost && questionnaireModalOpen && (
        <Modal
          componentName="saved-job-questionnaire"
          header={`Questionnaire for ${activeJobPost.job_title}`}
          subheader="Please help us improve your experience by answering the following questions."
          open={questionnaireModalOpen}
          renderButtonSet
          handleRightButtonClick={handleSaveQuestionnaire}
          rightButtonText="Save"
          onCloseButtonClick={() => {
            setQuestionnaireModalOpen(false);
            setActiveJobPost(null);
          }}
          leftButtonText="Cancel"
          disableRightButton={loading || isQuestionnaireIncomplete}
        >
          <Box
            componentName="saved-job-questionnaire-parent-row"
            border={false}
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            display="flex"
            rowGap={1}
          >
            <Box
              componentName="questionnaire-application-status-row"
              border={false}
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              display="flex"
              sx={{ mt: 2 }}
            >
              <TextField
                label="Application Status Comment"
                multiline
                fullWidth
                value={questionnaireData.application_status_comment}
                onChange={e =>
                  setQuestionnaireData(prev => ({
                    ...prev,
                    application_status_comment: e.target.value,
                  }))
                }
                helperText="Please provide a comment on your application status. Ex: Waiting to hear back from the recruiter, Interview scheduled for next week, etc."
              />
              <IconButton
                onClick={handleDeleteApplicationStatusComment}
                size="large"
                disabled={loading}
                sx={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              componentName="questionnaire-num-years-row"
              border={false}
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              display="flex"
            >
              <TextField
                label="Number of Years Experience"
                type="number"
                fullWidth
                value={questionnaireData.number_of_experienced_years ?? ""}
                onChange={e =>
                  setQuestionnaireData(prev => ({
                    ...prev,
                    number_of_experienced_years:
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10),
                  }))
                }
                helperText="Please provide an approximate number of years of experience you have for a job role like this. PS: it is okay if it is 0!"
              />
              <IconButton
                onClick={handleDeleteExperienceYears}
                size="large"
                disabled={loading}
                sx={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              componentName="questionnaire-saved-reason-row"
              border={false}
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              display="flex"
              sx={{ width: "100%" }}
            >
              <TextField
                label="Saved Reason"
                multiline
                fullWidth
                value={questionnaireData.saved_reason}
                onChange={e =>
                  setQuestionnaireData(prev => ({
                    ...prev,
                    saved_reason: e.target.value,
                  }))
                }
                helperText="Please provide your reason for saving this job post."
              />
              <IconButton
                onClick={handleDeleteSavedReason}
                size="large"
                disabled={loading}
                sx={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Modal>
      )}

      <Table
        componentName="saved-jobs"
        title="Saved Jobs"
        columns={columns}
        rows={savedJobs}
        onCompanyNameClick={handleCompanyClick}
        onJobPostClick={handleJobPostClick}
        onNoteClick={handleNoteClick}
        onApplicationStatusChange={handleApplicationStatusChange}
        handleQuestionnaireClick={handleQuestionnaireClick}
        isDisabled={isDisabled}
        isQuestionnaireComplete={isQuestionnaireComplete}
      />
    </Box>
  );
};
